import { useState, useEffect } from 'react';
import { workshopApi } from '../services/api';
import {
  Job,
  JobActivity,
  OperarioStatus,
  OperarioEstado,
  StepStatus,
  getOperarioEstadoName,
  getOperarioEstadoColor,
} from '../types/models';
import { format } from 'date-fns';
import {
  Car,
  Clock,
  UserCircle,
  Play,
  Pause,
  Coffee,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';

export default function MyWorkPage() {
  const [myStatus, setMyStatus] = useState<OperarioStatus | null>(null);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [activities, setActivities] = useState<JobActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activityNote, setActivityNote] = useState('');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const status = await workshopApi.getMyOperarioStatus() as OperarioStatus | null;
      setMyStatus(status);

      if (status?.job_id) {
        const job = await workshopApi.getMyCurrentJob() as Job | null;
        setCurrentJob(job);

        if (job?.id) {
          const jobDetail = await workshopApi.getJob(job.id) as Job;
          setCurrentJob(jobDetail);

          const acts = await workshopApi.getActivities(job.id) as JobActivity[];
          setActivities(acts || []);
        }
      } else {
        setCurrentJob(null);
        setActivities([]);
      }
    } catch (error) {
      console.error('Error loading my work:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartWork = async (stepId: string) => {
    if (!currentJob) return;
    setActionLoading(true);
    try {
      await workshopApi.startActivity(currentJob.id, stepId, activityNote);
      setActivityNote('');
      await loadData();
    } catch (error) {
      console.error('Error starting work:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async (tipo: 'pausa' | 'almuerzo') => {
    // Find active activity
    const activeActivity = activities.find(a => !a.fin);
    if (!activeActivity) return;

    setActionLoading(true);
    try {
      await workshopApi.pauseActivity(activeActivity.id, tipo);
      await loadData();
    } catch (error) {
      console.error('Error pausing:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    const activeActivity = activities.find(a => !a.fin);
    if (!activeActivity) return;

    setActionLoading(true);
    try {
      await workshopApi.resumeActivity(activeActivity.id);
      await loadData();
    } catch (error) {
      console.error('Error resuming:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFinish = async () => {
    const activeActivity = activities.find(a => !a.fin);
    if (!activeActivity) return;

    setActionLoading(true);
    try {
      await workshopApi.finishActivity(activeActivity.id, activityNote);
      setActivityNote('');

      // Complete the step if applicable
      if (currentJob) {
        const currentStep = currentJob.steps?.find(s => s.id === activeActivity.step_id);
        if (currentStep && currentStep.status_id === StepStatus.EN_CURSO) {
          await workshopApi.completeStep(currentJob.id, currentStep.id);
        }
      }

      await loadData();
    } catch (error) {
      console.error('Error finishing:', error);
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentStep = currentJob?.steps?.find(s =>
    s.status_id === StepStatus.EN_CURSO ||
    (myStatus?.stage_id && s.id === myStatus?.stage_id)
  );

  const completedSteps = currentJob?.steps?.filter(s => s.status_id === StepStatus.COMPLETADA) || [];
  const pendingSteps = currentJob?.steps?.filter(s => s.status_id === StepStatus.PENDIENTE) || [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <UserCircle className="h-6 w-6" />
          Mi Trabajo
        </h1>
        <p className="text-gray-500">
          {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy")}
        </p>
      </div>

      {/* Status Card */}
      <div className={`rounded-xl shadow-sm p-6 mb-6 ${
        myStatus?.status_id === OperarioEstado.TRABAJANDO
          ? 'bg-blue-50 border-2 border-blue-500'
          : myStatus?.status_id === OperarioEstado.LIBRE
          ? 'bg-green-50 border-2 border-green-500'
          : 'bg-gray-50 border-2 border-gray-300'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`w-4 h-4 rounded-full ${getOperarioEstadoColor(myStatus?.status_id as OperarioEstado)}`}></span>
            <span className="text-xl font-bold">
              {getOperarioEstadoName(myStatus?.status_id as OperarioEstado)}
            </span>
          </div>
          <Clock className="h-6 w-6 text-gray-400" />
        </div>

        {currentJob ? (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Car className="h-5 w-5 text-gray-400" />
                <span className="font-semibold text-lg">
                  {currentJob.vehicle?.placa} - {currentJob.vehicle?.marca} {currentJob.vehicle?.modelo}
                </span>
              </div>
              <p className="text-gray-500">
                Cliente: {currentJob.cliente_nombre}
              </p>

              {/* Progress */}
              {currentJob.steps && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Progreso</span>
                    <span className="text-sm text-gray-500">
                      {completedSteps.length}/{currentJob.steps.length} etapas
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all"
                      style={{ width: `${(completedSteps.length / currentJob.steps.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Current Step */}
              {currentStep && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Etapa actual:</strong> {currentStep.etapa?.nombre}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">No tienes trabajo asignado actualmente</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {currentJob && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Acciones</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => handlePause('pausa')}
              disabled={actionLoading || myStatus?.status_id === OperarioEstado.PAUSA}
              className="flex flex-col items-center gap-2 p-4 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <Pause className="h-8 w-8 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">Pausa</span>
              <span className="text-xs text-orange-600">(Café, WC)</span>
            </button>

            <button
              onClick={() => handlePause('almuerzo')}
              disabled={actionLoading || myStatus?.status_id === OperarioEstado.ALMUERZO}
              className="flex flex-col items-center gap-2 p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <Coffee className="h-8 w-8 text-gray-600" />
              <span className="text-sm font-medium text-gray-800">Almuerzo</span>
              <span className="text-xs text-gray-600">(1 hora)</span>
            </button>

            <button
              onClick={handleResume}
              disabled={actionLoading || (myStatus?.status_id !== OperarioEstado.PAUSA && myStatus?.status_id !== OperarioEstado.ALMUERZO)}
              className="flex flex-col items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <Play className="h-8 w-8 text-green-600" />
              <span className="text-sm font-medium text-green-800">Reanudar</span>
              <span className="text-xs text-green-600">Continuar trabajo</span>
            </button>

            <button
              onClick={handleFinish}
              disabled={actionLoading || !activities.some(a => !a.fin)}
              className="flex flex-col items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <CheckCircle className="h-8 w-8 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Terminar</span>
              <span className="text-xs text-blue-600">Fin etapa</span>
            </button>
          </div>

          {/* Note Input */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nota de actividad
            </label>
            <input
              type="text"
              value={activityNote}
              onChange={(e) => setActivityNote(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Describe lo que estás haciendo..."
            />
          </div>
        </div>
      )}

      {/* Pending Steps */}
      {currentJob && pendingSteps.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Etapas Pendientes</h2>
          <div className="space-y-2">
            {pendingSteps.map((step) => (
              <div
                key={step.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  <span>{step.etapa?.nombre}</span>
                  <span className="text-sm text-gray-500">
                    (~{step.etapa?.duracion_estimada} min)
                  </span>
                </div>
                <button
                  onClick={() => handleStartWork(step.id)}
                  disabled={actionLoading || myStatus?.status_id !== OperarioEstado.TRABAJANDO}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Iniciar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activities */}
      {activities.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Actividades Recientes</h2>
          <div className="space-y-3">
            {activities.slice(0, 10).map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {activity.status_id === 10 ? 'Inició' :
                       activity.status_id === 20 ? 'Pausó' :
                       activity.status_id === 30 ? 'Reanudó' :
                       activity.status_id === 40 ? 'Terminó' : 'Nota'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(activity.inicio), 'HH:mm')}
                    </span>
                  </div>
                  {activity.nota && (
                    <p className="text-sm text-gray-600 mt-1">{activity.nota}</p>
                  )}
                  {activity.duracion_minutos && (
                    <p className="text-sm text-gray-500">
                      Duración: {activity.duracion_minutos} min
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
